// Rename File Tool - Provides file and directory rename functions for workspace
// Extends WorkspaceTool - has access to appType and toolSystem
// Uses MoveFileTool internally via tool system

import { WorkspaceTool } from "../../base/WorkspaceTool.js";
import { logger } from "../../../../../../core/logger.js";

export class RenameFileTool extends WorkspaceTool {
  constructor() {
    super();
    this.toolName = 'renameFileTool';
  }
  /**
   * Rename a file or directory (stays in the same directory)
   * @param {string} path - Path to the file or directory to rename
   * @param {string} newName - New name for the file or directory (just the name, not a path)
   * @param {boolean} [overwrite=false] - Whether to overwrite existing destination
   * @returns {Promise<Object>} Result with operation status
   */
  async renameFile(path, newName, overwrite = false) {
    try {
      // Validate path
      if (!path || typeof path !== "string") {
        return this.returnError({}, "Valid file or directory path required");
      }

      // Validate newName
      if (!newName || typeof newName !== "string") {
        return this.returnError({}, "Valid new name required");
      }

      // Validate that newName is just a name, not a path
      if (newName.includes("/") || newName.includes("\\")) {
        return this.returnError(
          {},
          "New name must be a filename only, not a path. Use MoveFileTool to move to a different directory"
        );
      }

      // Validate that newName is not empty or just whitespace
      if (newName.trim().length === 0) {
        return this.returnError({}, "New name cannot be empty");
      }

      // Check if MoveFileTool is available
      if (!this.toolSystem?.moveFileTool) {
        return this.returnError(
          {},
          "MoveFileTool is not available in the tool system"
        );
      }

      // Extract directory from source path
      const normalizedPath = path.startsWith("/") ? path : `/${path}`;
      const pathParts = normalizedPath.split("/").filter(Boolean);
      pathParts.pop(); // Remove the filename to get just the directory
      const directory = pathParts.length > 0 ? `/${pathParts.join("/")}` : "/";

      // Construct destination path
      const destinationPath =
        directory === "/" ? `/${newName}` : `${directory}/${newName}`;

      // Use MoveFileTool to perform the rename
      const result = await this.toolSystem.moveFileTool.moveFile(
        path,
        destinationPath,
        overwrite
      );

      // If move was successful, return a rename-specific success message
      if (result.success) {
        logger.log(
          {
            tags: "tools|renamefile|info",
            color1: "lime",
          },
          `Renamed: ${path} -> ${newName}`,
          {
            originalPath: path,
            newName,
            destinationPath: result.data.destination,
          }
        );

        return this.returnSuccess(
          {
            path: result.data.destination,
            originalPath: path,
            newName,
            type: result.data.type,
            overwrite,
          },
          `${result.data.type === "directory" ? "Directory" : "File"} renamed successfully`
        );
      }

      // If move failed, return the error
      return result;
    } catch (error) {
      logger.error(
        {
          tags: "tools|renamefile|error",
          color1: "red",
        },
        `Failed to rename: ${path} -> ${newName}`,
        error
      );
      return this.returnError({}, error.message);
    }
  }
}
